import { Outlet, NavLink, useNavigate, useLocation } from "react-router";
import {
  Users,
  Package,
  UserCog,
  DollarSign,
  MapPin,
  Map as MapIcon,
  Truck,
  Route,
  CreditCard,
  Image,
  BarChart3,
  Settings2,
  BookUser,
  Megaphone,
  Bell,
  ChevronDown,
  ChevronRight,
  FileText,
  Receipt,
  ClipboardList,
  Globe,
  Clock,
  Boxes,
  Building2,
  CalendarDays,
  XCircle,
  Edit,
  Cog,
  ListTodo,
  SlidersHorizontal,
  Tag,
  Gift,
  ShieldCheck,
  PenLine,
  Sparkles,
  Percent,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { usePendingCancellations } from "@/api/orders";
import { ProtectedRoute } from "@/components/protected-route";
import { CarrierFilter } from "@/components/carrier-filter";
import { DispatchCenterFilter } from "@/components/dispatch-center-filter";

interface SidebarChildItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface SidebarItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: SidebarChildItem[];
}

interface SidebarGroup {
  label: string;
  items: SidebarItem[];
}

// Carrier-scope foundation Phase 1: nav split into two top-level groups.
// Operations = per-carrier work (scope-aware in Phase 2).
// Platform   = EZX-managed substrate (scope-agnostic).
const sidebarGroups: SidebarGroup[] = [
  {
    label: "Operations",
    items: [
      {
        to: "fulfillment",
        label: "Fulfillment",
        icon: Package,
        children: [
          { to: "fulfillment/orders", label: "Orders", icon: Package },
          { to: "fulfillment/shipments", label: "Shipments", icon: Truck },
          {
            to: "fulfillment/cancellations",
            label: "Cancellations",
            icon: XCircle,
          },
          {
            to: "fulfillment/change-requests",
            label: "Change Requests",
            icon: Edit,
          },
          {
            to: "refund-requests",
            label: "Refund Requests",
            icon: Receipt,
          },
        ],
      },
      {
        to: "carriers",
        label: "Carriers",
        icon: Settings2,
        children: [
          { to: "carriers", label: "API Providers", icon: Globe },
          { to: "carriers/list", label: "Carrier Configuration", icon: Truck },
          {
            to: "carriers/customer-discounts",
            label: "Customer Discounts",
            icon: Percent,
          },
          {
            to: "carriers/dispatch-centers",
            label: "Dispatch Centers",
            icon: MapPin,
          },
        ],
      },
      {
        to: "team",
        label: "Team",
        icon: UserCog,
        children: [{ to: "team", label: "Operators", icon: UserCog }],
      },
      { to: "scheduling", label: "Scheduling", icon: CalendarDays },
      // Pricing-merger Phase 8: rate bands collapsed into one workspace (carrier
      // label + pickup leg + redist leg, scoped by carrier service).
      //
      // Flat, not a group: this was a "Pricing" group wrapping exactly ONE child,
      // and PLATFORM has its own "Pricing" group. Two identical words, two
      // destinations, and no way to tell them apart from the sidebar.
      { to: "pricing", label: "Rate workspace", icon: Truck },
      {
        to: "add-on-services",
        label: "Add-On Services",
        icon: Boxes,
        children: [
          { to: "add-on-services", label: "Pickup Services", icon: Truck },
          {
            to: "add-on-services/insurance",
            label: "Insurance Services",
            icon: ShieldCheck,
          },
          {
            to: "add-on-services/packaging",
            label: "Packaging Services",
            icon: Package,
          },
          {
            to: "add-on-services/signature",
            label: "Signature Services",
            icon: PenLine,
          },
          {
            to: "add-on-services/special-handling",
            label: "Special Handling Services",
            icon: Sparkles,
          },
        ],
      },
      {
        to: "pickup-bookings",
        label: "Pickups",
        icon: Truck,
        children: [{ to: "pickup-bookings", label: "Bookings", icon: Truck }],
      },
      {
        to: "trips",
        label: "Trips",
        icon: Route,
        children: [
          { to: "trips", label: "Trips", icon: Route },
          { to: "trips/tasks", label: "Tasks", icon: ClipboardList },
        ],
      },
      { to: "analytics", label: "Analytics", icon: BarChart3 },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "customers", label: "Customers", icon: Users },
      { to: "contacts", label: "Address Book", icon: BookUser },
      {
        to: "service-areas",
        label: "Service Areas",
        icon: MapPin,
        children: [
          { to: "service-areas", label: "Areas", icon: MapPin },
          { to: "service-areas/boundaries", label: "Boundaries", icon: Globe },
        ],
      },
      {
        to: "pricing/fx-rates",
        // NOT "Pricing" — the rate workspace under Operations is also "pricing",
        // and a sidebar with the same word twice tells you nothing about which
        // one you want. These are the platform-wide knobs: FX, tax, surcharges,
        // discounts. Carrier rates live in the Rate workspace.
        label: "Pricing settings",
        icon: DollarSign,
        children: [
          { to: "pricing/fx-rates", label: "FX Rates", icon: DollarSign },
          {
            to: "pricing/property-type-surcharges",
            label: "Property Type Surcharges",
            icon: Building2,
          },
          { to: "pricing/tax-rates", label: "Tax Rates", icon: Percent },
          {
            to: "pricing/discounts",
            label: "Discounts",
            icon: Gift,
          },
        ],
      },
      { to: "team/users", label: "Users", icon: Users },
      // Flat, and renamed: this was an "Add-On Services" group wrapping exactly
      // ONE child, while Operations has the real Add-On Services group (pickup,
      // insurance, packaging, signature, special handling). Two identical labels
      // for two different things.
      {
        to: "add-on-services/grants",
        label: "Add-on grants",
        icon: ShieldCheck,
      },
      {
        to: "payments",
        label: "Payments",
        icon: CreditCard,
        children: [
          { to: "payments", label: "Transactions", icon: DollarSign },
          { to: "payments/receipts", label: "Receipts", icon: Receipt },
          { to: "payments/invoices", label: "Invoices", icon: FileText },
          {
            to: "payments/stuck-orders",
            label: "Stuck Orders",
            icon: ClipboardList,
          },
          {
            to: "payments/upcoming-invoices",
            label: "Upcoming Invoices",
            icon: Clock,
          },
          {
            to: "payments/adjustments",
            label: "Adjustments",
            icon: Tag,
          },
          { to: "payments/settings", label: "Settings", icon: Settings2 },
          { to: "payments/config", label: "Config", icon: CreditCard },
        ],
      },
      { to: "address-medias", label: "Address Medias", icon: Image },
      { to: "announcements", label: "Announcements", icon: Megaphone },
      { to: "notification-logs", label: "Notification Logs", icon: Bell },
      {
        to: "system",
        label: "System",
        icon: Cog,
        children: [
          { to: "system/queues", label: "Job Queues", icon: ListTodo },
          {
            to: "system/platform-settings",
            label: "Platform settings",
            icon: SlidersHorizontal,
          },
        ],
      },
    ],
  },
];

function SidebarNavItem({
  item,
  badgeCounts,
}: {
  item: SidebarItem;
  badgeCounts?: Record<string, number>;
}) {
  const location = useLocation();

  // Check if current path matches any child route
  const isChildActive = useCallback(
    (children: SidebarChildItem[] | undefined) => {
      if (!children) return false;
      return children.some((child) =>
        location.pathname.startsWith(`/${child.to}`)
      );
    },
    [location.pathname]
  );

  const [isExpanded, setIsExpanded] = useState(() => {
    // Auto-expand if current path matches any child
    if (item.children) {
      return isChildActive(item.children);
    }
    return false;
  });

  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;

  // Check if this parent or any child is active
  const isParentActive = hasChildren && isChildActive(item.children);

  // Auto-expand when navigating to a child route
  useEffect(() => {
    if (hasChildren && isChildActive(item.children)) {
      setIsExpanded(true);
    }
  }, [item.children, hasChildren, isChildActive]);

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "w-full flex items-center justify-between gap-3 px-4 py-2 rounded-md transition-colors",
            isParentActive
              ? "bg-gray-200 text-gray-900"
              : "text-gray-600 hover:bg-gray-100"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5" />
            {item.label}
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {isExpanded && item.children && (
          <div className="ml-4 mt-1 space-y-1">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const badgeCount = badgeCounts?.[child.to];
              return (
                <NavLink
                  key={child.to}
                  to={child.to}
                  end={child.to === item.to} // Only exact match for parent route
                  className={({ isActive }) =>
                    cn(
                      "flex items-center justify-between px-4 py-2 rounded-md transition-colors text-sm",
                      isActive
                        ? "bg-gray-900 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    )
                  }
                >
                  <div className="flex items-center gap-3">
                    <ChildIcon className="h-4 w-4" />
                    {child.label}
                  </div>
                  {badgeCount !== undefined && badgeCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium bg-red-500 text-white rounded-full">
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-4 py-2 rounded-md transition-colors",
          isActive
            ? "bg-gray-900 text-white"
            : "text-gray-600 hover:bg-gray-100"
        )
      }
    >
      <Icon className="h-5 w-5" />
      {item.label}
    </NavLink>
  );
}

export function TableLayout() {
  const navigate = useNavigate();
  const { data: pendingCancellations } = usePendingCancellations();

  // Build badge counts for sidebar items
  const badgeCounts: Record<string, number> = {
    "fulfillment/cancellations": pendingCancellations?.data?.length ?? 0,
  };

  useEffect(() => {
    function handleKeyPress(event: KeyboardEvent) {
      // Ignore if user is typing in an input or textarea
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA")
      ) {
        return;
      }

      // Handle keyboard shortcuts
      if (event.key === "M") {
        void navigate("/dispatch");
      }
    }

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [navigate]);

  return (
    <ProtectedRoute>
      <div className="flex flex-col h-screen">
        {/* Header */}
        <header className="bg-black border-b shadow-xs z-10">
          <div className="px-4 sm:px-24 py-4 flex justify-between items-center">
            <h1 className="text-xl font-semibold text-white">
              Admin Dashboard
            </h1>
            <div className="flex items-center gap-4">
              <CarrierFilter />
              <DispatchCenterFilter />
              <NavLink
                to="/dispatch"
                className="flex items-center gap-2 text-white hover:text-gray-200"
                title="Go back to dispatch (M)"
              >
                <MapIcon className="w-5 h-5" />
                Go back to dispatch
              </NavLink>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <aside className="w-64 bg-gray-50 border-r shrink-0 overflow-y-auto">
            <nav className="p-4 space-y-4">
              {sidebarGroups.map((group) => (
                <div key={group.label} className="space-y-1">
                  <div className="px-4 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    <SidebarNavItem
                      key={`${group.label}:${item.to}:${item.label}`}
                      item={item}
                      badgeCounts={badgeCounts}
                    />
                  ))}
                </div>
              ))}
            </nav>
          </aside>

          {/* Content Area */}
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
}
